/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, no-unused-vars, warp-drive/no-legacy-request-patterns */
// Extends From
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { isArray } from '@ember/array';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import { AdapterError } from '@warp-drive/legacy/adapter/error';
import { getOAuth } from '@linc-technologies/oauth2';
import config from '@linc-technologies/linced-common/utils/config';
import {
  decodeState,
  signIn,
} from '@linc-technologies/linced-common/utils/oauth2';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
// Internal Imports

// reauthErrors lists the OAuth error codes that mean the stored refresh token
// is definitively dead (burnt by single-use rotation, revoked, or rejected for
// this client). Retrying cannot revive these; anything else (server_error,
// network failures) is treated as transient and retried.
const reauthErrors = [
  'invalid_grant',
  'invalid_client',
  'unauthorized_client',
  'invalid_request',
];

// reauthPendingTimeoutMs bounds how long a sibling tab will wait after
// hearing 'reauth-pending' before reloading unconditionally. Generous enough
// to cover a normal authorize -> (silent) IdP auth -> callback round trip,
// but short enough that a dead SSO session or a closed originating tab
// doesn't leave other tabs hanging indefinitely (see reauthenticate()).
const reauthPendingTimeoutMs = 20000;

// Loop-guard counters (see consumeGuardAttempt). Each bounds how many times
// its flow retries before falling back to logout(). They live in
// sessionStorage so they survive the full-page redirects these flows trigger
// while staying per-tab:
//
//   session:loginAttemptCount   - callback failures retrying login
//                                 (startup.js); cleared on callback success.
//   session:fetch401Count       - 401-triggered re-auths (fetch.js); cleared
//                                 only by a successful response from a
//                                 trusted (authenticated) host, since that's
//                                 the only kind of response that actually
//                                 proves our tokens work.
//   session:reauthCount         - reauthenticate() redirects; cleared only
//                                 by that same trusted-host API success
//                                 (fetch.js), NOT by getToken() succeeding:
//                                 every reauth cycle that reaches the
//                                 callback necessarily just obtained a
//                                 token, so clearing it there would make the
//                                 logout('reauthLoop') fallback unreachable.
//   session:refreshReloadCount  - reloads after transient refresh failure;
//                                 cleared on getToken() success.
//   session:contextReloadCount  - reloads after context-load failure;
//                                 cleared once context loads.
//
// session:notifyOtherTabsOnCallback is a flag, not a counter, and lives in
// localStorage rather than sessionStorage: set by reauthenticate() so
// whichever tab's /callback completes the exchange broadcasts a
// session-replaced ('reauth') message to other tabs once fresh tokens
// actually exist. It must be readable from a tab other than the one that set
// it, because the completing tab isn't necessarily the one that called
// reauthenticate() - a sibling tab's 'reauth-pending' fallback (see
// _scheduleReauthPendingTimeout) can reload and win the race with its own
// independent login(), landing on /callback with no memory of why. Cleared
// by whichever /callback consumes it first.
// reauthenticate() also broadcasts 'reauth-pending' immediately (before that
// point), so other tabs arm a bounded fallback reload instead of hanging.

export default class SessionService extends Service {
  // Services
  @service
  logging;

  @service
  options;

  @service
  router;

  @service
  store;

  @service
  toaster;

  // Properties
  @tracked
  previousToken = '';

  @tracked
  roles = [];
  @tracked
  scopes = [];
  @tracked
  userId = '';
  user = null;
  @tracked
  personId = '';
  person = null;
  @tracked
  tenants = [];
  @tracked
  schoolId = '';
  @tracked
  school = null;

  // oauth stores an OAuth client for communicating with and processing
  // responses from an OAuth provider.
  @tracked
  oauth = null;

  @tracked
  sessionChannel = null;

  // _reauthPendingTimeoutId tracks the fallback reload timer scheduled on a
  // 'reauth-pending' broadcast, so it can be cancelled once the follow-up
  // 'reauth'/'logout' message actually arrives. See reauthPendingTimeoutMs.
  @tracked
  _reauthPendingTimeoutId = null;

  // reauthInProgress is true once reauthenticate() has started redirecting.
  // Concurrent failures (e.g. several API calls receiving 401 at once) check
  // this to avoid stacking purges, redirects, and loop-guard increments.
  @tracked
  reauthInProgress = false;

  // mfaEnabled specifies if the user has MFA enabled.
  @alias('user.isMfaEnrolled')
  mfaEnabled;

  // Backwards compat
  @alias('schoolId')
  activeSchool;

  @tracked
  _contextReloadScheduled;

  @computed
  get resetAccessUrl() {
    return config.oauth ? `${config.oauth.host}/reset-access` : null;
  }

  // TODO: remove once school switching is ready for all users
  // Uses user model scopes (not JWT scopes) so downscoped JWTs don't hide
  // the button for Hero staff.
  @computed('user.scopes.[]')
  get isHeroStaff() {
    const scopes = this.user?.scopes;
    return (
      (scopes?.includes(Scopes.LINCED_MANAGER) ||
        scopes?.includes(Scopes.LINCED_ADMIN)) ??
      false
    );
  }

  init() {
    super.init(...arguments);
    // Set oauth instance
    this.oauth = getOAuth();

    // Set the oauth logger callbacks so we use centralized logging
    this.oauth.logger.setCallbacks(
      (value, func, context) => this.logging.pushLog(value, func, context),
      (value, func, context) => this.logging.pushError(value, func, context),
      (value) => this.logging.pushEvent(value),
    );

    // Set up cross tab broadcast channel for sending logout events.
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel('session-events');
      channel.onmessage = (event) => {
        switch (event?.data?.type) {
          case 'logout':
            this._clearReauthPendingTimeout();
            this.logout('otherTab', false);
            break;
          case 'reauth-pending':
            // Another tab purged the shared tokens (dead locally, but the
            // IdP SSO session may still be alive) and is re-authenticating.
            // It broadcasts 'reauth' once fresh tokens actually exist, but
            // if that tab's SSO session is dead (lands on the login page)
            // or the tab is closed mid-flow, no such message ever comes:
            // without this bound, this tab would otherwise hang forever
            // inside the oauth lib's code-exchange wait the next time it
            // needs a token. Reload unconditionally once the window elapses;
            // the startup flow resolves correctly either way (fresh tokens,
            // or a fresh login redirect).
            this._scheduleReauthPendingTimeout();
            break;
          case 'reauth':
            // Another tab's re-authentication completed and fresh tokens now
            // exist. Reload so this tab runs the startup auth flow: it
            // either finds the fresh tokens or silently re-auths against the
            // still-alive SSO session itself.
            this._clearReauthPendingTimeout();
            window.location.reload();
            break;
          case 'school-switch':
            this._clearReauthPendingTimeout();
            window.location.replace('/school-switching');
            break;
        }
      };
      this.sessionChannel = channel;
    } else {
      this.logging.pushError('broadcastChannel unavailable', 'session:init');
      this.toaster.error(
        'Please update your browser, HERO may not function correctly.',
      );
    }

    // Ask the browser to mark our origin's storage as persistent so the
    // tokens in IndexedDB are not evicted under storage pressure or by
    // Safari's script-writable-storage cap. Best effort; denial just means
    // eviction stays possible, so log the outcome for diagnosing
    // vanished-token reports.
    this._requestPersistentStorage();
  }

  // _requestPersistentStorage requests persistent storage only when doing so
  // is silent. Chromium decides by heuristics and reports the decision via
  // the Permissions API without prompting; Safari has no Permissions API
  // entry for this but never prompts on persist(); Firefox shows a modal
  // permission prompt when the permission state is 'prompt', which we must
  // not trigger, so we skip the request there.
  async _requestPersistentStorage() {
    if (!navigator.storage?.persist) {
      return;
    }
    try {
      let permissionState;
      try {
        const permission = await navigator.permissions?.query({
          name: 'persistent-storage',
        });
        permissionState = permission?.state;
      } catch (e) {
        // Permissions API unavailable or the permission name unsupported
        // (Safari): persist() is safe to call, it never prompts there.
        permissionState = undefined;
      }
      if (permissionState === 'prompt' || permissionState === 'denied') {
        // 'prompt' would show a modal (Firefox); 'denied' would be a no-op.
        if (this.isDestroying || this.isDestroyed) return;
        this.logging.pushLog(
          `storage persistence not requested, permission state: ${permissionState}`,
          'session:init',
        );
        return;
      }
      const persisted =
        (await navigator.storage.persisted()) ||
        (await navigator.storage.persist());
      // The promises can resolve after the service is torn down (e.g. in
      // tests); don't touch injected services once destroyed.
      if (this.isDestroying || this.isDestroyed) return;
      this.logging.pushLog(
        `storage persistence granted: ${persisted}`,
        'session:init',
      );
    } catch (error) {
      if (this.isDestroying || this.isDestroyed) return;
      this.logging.pushError(error, 'session:init');
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    // Close broadcast channel
    this.sessionChannel?.close();
    this._clearReauthPendingTimeout();
  }

  // _scheduleReauthPendingTimeout arms the fallback reload for a
  // 'reauth-pending' broadcast (see init()'s channel listener). Idempotent:
  // an already-armed timer is left alone rather than restarted.
  _scheduleReauthPendingTimeout() {
    if (this._reauthPendingTimeoutId) return;
    this._reauthPendingTimeoutId = setTimeout(() => {
      this._reauthPendingTimeoutId = null;
      window.location.reload();
    }, reauthPendingTimeoutMs);
  }

  // _clearReauthPendingTimeout cancels a pending fallback reload once the
  // originating tab's outcome ('reauth' or 'logout') actually arrives.
  _clearReauthPendingTimeout() {
    if (!this._reauthPendingTimeoutId) return;
    clearTimeout(this._reauthPendingTimeoutId);
    this._reauthPendingTimeoutId = null;
  }

  async isAuthenticated() {
    try {
      return this.oauth.isAuthenticated();
    } catch (error) {
      this.logging.pushError(error, 'isAuthenticated');
      return false;
    }
  }

  // storedAccessToken returns the currently stored access token, or undefined
  // if there isn't a live one, without triggering a refresh. Used to detect
  // whether a callback actually replaced the session (see startup.js).
  async storedAccessToken() {
    return this.oauth.getAccessToken();
  }

  async ensure() {
    // we can just await getToken since we know that it will only return
    // once the onToken callback has run and set all our user info here.
    // TODO: just await a promise that gets resolved at the end of onToken. Would
    //  be a bit cleaner.
    await this.getToken();
  }

  async doCallback() {
    let callbackData = await this.oauth.getCallbackResponse();
    if (!callbackData || callbackData.error) {
      // Surface the OAuth error code (e.g. invalid_grant for a stale/reused
      // code) so callers can tell a definitively dead code apart from a
      // genuine failed sign-in attempt, rather than treating every failure
      // the same way.
      const error = new Error('error failed to exchange code for token');
      error.oauthErrorCode =
        callbackData?.codeResponse?.error ?? callbackData?.response?.error;
      throw error;
    }
    // Only allow relative return URLs to prevent open redirect via crafted state.
    const state = callbackData.response.state
      ? decodeState(callbackData.response.state)
      : {};
    const returnUrl =
      typeof state?.returnUrl === 'string' && state.returnUrl.startsWith('/')
        ? state.returnUrl
        : undefined;
    return {
      token: callbackData.token,
      returnUrl,
    };
  }

  /**
   * getToken returns a promise that will resolve with a token, or undefined.
   *
   * @public
   * @return {Promise<OAuthTokenV1 | undefined>}
   */
  async getToken() {
    // Retry delays sized to cover a laptop waking from sleep: Wi-Fi commonly
    // takes tens of seconds to renegotiate while navigator.onLine already
    // reports true, so transient failures must survive well past that window.
    const retryDelays = [1000, 5000, 15000, 30000];
    const maxAttempts = retryDelays.length + 1;
    let tokenWrapper;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // The owner can be destroyed while an attempt waits (an app teardown,
      // or the end of a test). Stop: a destroyed service cannot look up the
      // logging service.
      if (this.isDestroying || this.isDestroyed) {
        return undefined;
      }
      // Don't burn retry attempts while the device knows it is offline.
      await this._waitForConnectivityIfOffline(
        'network is down waiting for connectivity to retry token fetch',
        'getToken',
      );
      try {
        tokenWrapper = await this.oauth.getToken();
        if (!tokenWrapper) {
          throw new Error('error failed to get token, token undefined');
        }
        break;
      } catch (error) {
        if (this.isDestroying || this.isDestroyed) {
          return undefined;
        }
        this.logging.pushError(error, `getToken:attempt${attempt}`);
        // A definitive OAuth error means the refresh token is dead: retrying
        // cannot revive it. The IdP SSO session is likely still alive though,
        // so re-authenticate via the authorize endpoint (silent token
        // re-issue) instead of logging out, which would kill the SSO session
        // and force a full credential login.
        const authErrorCode = this._refreshAuthErrorCode(error);
        if (authErrorCode) {
          return this.reauthenticate(`getToken:${authErrorCode}`);
        }
        if (attempt === maxAttempts) {
          // The device may have gone offline mid-attempt; if so, block for
          // connectivity (which reloads) rather than burning one of the
          // transient-failure reload attempts on an offline error.
          await this._waitForConnectivityIfOffline(
            'network is down waiting for connectivity to retry token fetch',
            'getToken',
          );
          return this._handleTransientTokenFailure(maxAttempts);
        }
        await new Promise((resolve) =>
          setTimeout(resolve, retryDelays[attempt - 1]),
        );
      }
    }

    // Token obtained: clear the transient-refresh-reload counter so it
    // doesn't carry over into future refresh failures. reauthCount is
    // deliberately NOT cleared here: every reauth cycle that reaches this
    // point necessarily just obtained a token, so clearing it here would
    // make logout('reauthLoop') unreachable for a systemic reauth failure.
    // It's only cleared by fetch.js after a genuinely successful
    // authenticated API call, which is the real signal that the session is
    // healthy again.
    sessionStorage.removeItem('session:refreshReloadCount');

    const reloadCountKey = 'session:contextReloadCount';
    const maxContextReloads = 3;
    try {
      await this.onToken(tokenWrapper);
      // Clear the reload counter on success so it doesn't carry over to future
      // context loads (e.g. after a token refresh).
      sessionStorage.removeItem(reloadCountKey);
    } catch (error) {
      this.logging.pushError(error, 'getToken:onToken');
      if (error instanceof AdapterError) {
        // HTTP error loading context, logout and block forever. logout()
        // calls window.location.replace() which is non-blocking, so execution
        // would otherwise continue and callers could trigger login() before
        // the browser navigates away, causing a re-login loop.
        sessionStorage.removeItem(reloadCountKey);
        await this.logout('contextLoadHttpError');
      }
      // Likely a network error. Schedule a reload so the app can recover;
      // token is still in IDB so the user won't be logged out. Guard against
      // scheduling multiple reloads within the same page load.
      if (!this._contextReloadScheduled) {
        this._contextReloadScheduled = true;

        // If the device is offline, wait for connectivity to return and reload
        // without counting against the limit — this isn't an app error.
        await this._waitForConnectivityIfOffline(
          'network is down waiting for connectivity to reload',
          'getToken:onToken',
        );

        // The sessionStorage counter stops the reload loop across page loads.
        if (this.consumeGuardAttempt(reloadCountKey, maxContextReloads)) {
          setTimeout(() => window.location.reload(), 3000);
        } else {
          this.logging.pushError(
            `logging out: context reload failed after ${maxContextReloads} attempts`,
            'getToken:onToken',
          );
          await this.logout('contextLoadMaxReloads');
        }
      }
    }
    return tokenWrapper;
  }

  // If the device is offline, logs the error, registers an online listener,
  // and returns a never-resolving Promise so the caller blocks until the page
  // reloads. Re-checks navigator.onLine after registering to close the race
  // window between the offline check and listener registration. Returns
  // undefined (awaitable, resolves immediately) when the device is online.
  _waitForConnectivityIfOffline(message, context) {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      return;
    }
    this.logging.pushError(message, context);
    window.addEventListener('online', () => window.location.reload(), {
      once: true,
    });
    if (navigator.onLine) {
      window.location.reload();
    }
    return new Promise(() => {});
  }

  // _refreshAuthErrorCode returns the OAuth error code when the given error
  // means the refresh token is definitively dead, or undefined for transient
  // errors. The oauth library rejects with either an IOAuthResponse-shaped
  // object (`error`) or an OAuthResponseError (`oauthError`) depending on
  // library version, so check both.
  _refreshAuthErrorCode(error) {
    const code = error?.oauthError ?? error?.error;
    return reauthErrors.includes(code) ? code : undefined;
  }

  // consumeGuardAttempt increments the loop-guard counter stored in
  // sessionStorage under key and returns true while it remains under max.
  // Once max attempts have been consumed it resets the counter and returns
  // false so the caller can fall back (usually to logout()). See the counter
  // overview at the top of this file.
  consumeGuardAttempt(key, max) {
    const count = parseInt(sessionStorage.getItem(key) || '0', 10);
    if (count >= max) {
      sessionStorage.removeItem(key);
      return false;
    }
    sessionStorage.setItem(key, String(count + 1));
    return true;
  }

  // _handleTransientTokenFailure is reached when the token refresh keeps
  // failing with network/server errors despite ~50 seconds of retries while
  // the device claims to be online. The refresh token itself is still valid,
  // so keep it and reload the page to retry from scratch, guarded by a
  // sessionStorage counter. Only after several failed page loads do we fall
  // back to logging out.
  async _handleTransientTokenFailure(attempts) {
    const maxReloads = 3;
    if (this.consumeGuardAttempt('session:refreshReloadCount', maxReloads)) {
      this.logging.pushError(
        `token refresh failed ${attempts} times while online, reloading to retry`,
        'getToken',
      );
      setTimeout(() => window.location.reload(), 5000);
      // Block forever so callers can't continue with an unauthenticated
      // session; the tokens stay in IDB for the next page load to retry.
      return new Promise(() => {});
    }
    this.logging.pushError(
      `logging out: token refresh failed across ${maxReloads} reloads`,
      'getToken',
    );
    await this.logout('getTokenTransientExhausted');
    return undefined;
  }

  // reauthenticate purges the dead local tokens and redirects to the OAuth
  // authorize endpoint instead of the IdP logout. If the FusionAuth SSO
  // session is still alive (30-day sliding window), the user is silently
  // issued fresh tokens and returned to the current URL without re-entering
  // credentials; otherwise they land on the login page. A per-tab counter
  // guards against a systemic auth failure redirect-looping forever.
  async reauthenticate(reason) {
    // Only the first caller drives the flow; later callers just block until
    // the redirect happens.
    if (this.reauthInProgress) {
      return new Promise(() => {});
    }
    this.reauthInProgress = true;

    this.logging.pushEvent(`session:reauthenticate:${reason}`);

    const maxReauthAttempts = 3;
    if (!this.consumeGuardAttempt('session:reauthCount', maxReauthAttempts)) {
      this.logging.pushError(
        `logging out: re-auth looped ${maxReauthAttempts} times`,
        'reauthenticate',
      );
      await this.logout('reauthLoop');
      return new Promise(() => {});
    }

    try {
      // Local purge only. Unlike logout(), this does NOT hit the IdP /logout
      // endpoint, so the SSO session survives to silently re-issue tokens.
      await this.purgeTokens();

      // Other tabs share the now-purged IDB: without a signal they'd only
      // notice once they themselves needed a token, and would then hang
      // inside the oauth lib's code-exchange wait rather than erroring.
      // 'reauth-pending' arms a bounded fallback reload in those tabs (see
      // init()'s channel listener) instead of sending them straight to the
      // startup flow now, which would race their own authorize redirect
      // against this one while storage is still empty. The /callback route
      // broadcasts the follow-up 'reauth' once doCallback() confirms a real
      // token, which cancels that fallback.
      this.sessionChannel?.postMessage({ type: 'reauth-pending' });
      localStorage.setItem('session:notifyOtherTabsOnCallback', '1');

      await signIn();
    } catch (error) {
      // Tokens may already be purged at this point (e.g. signIn()'s
      // authorizeUri() does unguarded IndexedDB writes, and this whole flow
      // exists because that storage is flaky). Continuing would leave the
      // tab with no tokens, no redirect, and every future getToken() call
      // blocked forever, so fall back to a full logout instead.
      this.logging.pushError(error, 'reauthenticate');
      await this.logout('reauthFailed');
      return new Promise(() => {});
    }
    // signIn() redirects via window.location.replace, which is non-blocking.
    // Block forever so callers can't continue and trigger competing flows
    // before the browser navigates away.
    return new Promise(() => {});
  }

  // notifySessionReplaced tells other tabs the tokens in the shared IDB were
  // replaced (or purged for replacement); they reload through the startup
  // auth flow to pick up the new session rather than operating on stale
  // in-memory identity.
  notifySessionReplaced() {
    this.sessionChannel?.postMessage({ type: 'reauth' });
  }

  async logout(reason, originator = true) {
    this.logging.pushEvent(`session:logout:${reason}`);

    // The FA logout endpoint.
    //
    // `skip` is interpreted by our FusionAuth proxy, not FusionAuth itself.
    // It tells the proxy to skip clearing tokens for the originating app
    // (identified by modulePrefix), since we purge those tokens ourselves
    // via purgeTokens(). It does NOT suppress IdP session invalidation.
    const signoutURI =
      `${config.oauth.baseUri}/logout?` +
      `client_id=${config.oauth.clientId}` +
      `&tenantId=${config.oauth.tenantId}` +
      `&skip=${config.modulePrefix}`;

    try {
      if (!originator) {
        // if not originator logout was initiated from a different tab,
        // we can just set window location to our logged-out route, since
        // the originator tab handled all the actual logic.
        window.location.replace(`${window.location.origin}/logged-out`);
        return;
      }
      // purge all tokens in idb
      await this.purgeTokens();
      // kick off other tabs logout process
      if (this.sessionChannel) {
        this.sessionChannel.postMessage({
          type: 'logout',
        });
      }

      // redirect to FA logout
      window.location.replace(signoutURI);
    } catch (e) {
      this.logging.pushError(e, 'logout');
      // Redirect to the IdP logout even when the local purge failed:
      // invalidating the SSO session (which also revokes our refresh
      // tokens server-side) matters more than the local purge, which the
      // logged-out page retries on load anyway. Stalling here would leave
      // a user who believes they logged out with a live SSO session.
      window.location.replace(signoutURI);
    } finally {
      // Block forever so callers can never fall through and trigger a
      // re-login loop. The browser discards this promise once it navigates
      // away.
      // eslint-disable-next-line no-unsafe-finally
      return new Promise(() => {});
    }
  }

  /**
   * purgeTokens removes token data from the underlying stores.
   *
   * Rethrows on failure rather than navigating itself: callers (logout(),
   * reauthenticate()) each drive their own navigation on error, and this
   * used to race its own '/logged-out' redirect against theirs.
   *
   * @return {Promise<void>}
   */
  async purgeTokens() {
    this.logging.pushEvent(`session:purgeTokens`);
    try {
      await this.oauth.purgeAll();
    } catch (error) {
      this.logging.pushError(error, 'purgeTokens');
      throw error;
    }
  }

  async switchSchool() {
    this.sessionChannel?.postMessage({ type: 'school-switch' });
    try {
      await this.oauth.purgeAll();
    } catch (error) {
      this.logging.pushError(error, 'switchSchool');
    }
    window.location.href = this.resetAccessUrl;
  }

  async handleSpringboardMessage(data) {
    try {
      if (!isEmpty(data?.accessToken)) {
        this.logging.pushLog(
          'access token received from wrapper',
          'handleSpringboardMessage',
        );
      }
      if (!isEmpty(data?.refreshToken)) {
        this.logging.pushLog(
          'refresh token received from wrapper',
          'handleSpringboardMessage',
        );
      }

      let returnUrl = data?.returnUrl;
      this.logging.pushLog(
        `returnUrl received: ${returnUrl}`,
        'handleSpringboardMessage',
      );

      await this.oauth.setTokens(data?.accessToken, data?.refreshToken);
      // Only allow string typed & relative return URLs to prevent open redirect.
      if (typeof returnUrl !== 'string') {
        returnUrl = '/';
        this.logging.pushError(
          'returnUrl is not of type string',
          'handleSpringboardMessage',
        );
      }
      if (!returnUrl.startsWith('/')) {
        returnUrl = '/';
        this.logging.pushLog(
          'returnUrl is not relative',
          'handleSpringboardMessage',
        );
      }

      // Ensure we are set up fully before going to return URL.
      await this.ensure();
      this.router.replaceWith(returnUrl);
    } catch (error) {
      this.logging.pushError(error, 'session:springboard');
      throw error;
    }
  }

  async onToken(tokenWrapper) {
    if (isEmpty(tokenWrapper)) {
      return;
    }

    if (tokenWrapper.token === this.previousToken) {
      return;
    }

    if (isEmpty(this.previousToken)) {
      this.logging.pushEvent('onToken:tokenInit');
    } else {
      this.logging.pushEvent('onToken:tokenRefresh');
    }

    await this.initializeFromToken(tokenWrapper);
  }

  async initializeFromToken(tokenWrapper) {
    this.previousToken = tokenWrapper.token;

    this.applyClaims(tokenWrapper.claims);

    await this.loadContext();

    this.logging.pushEvent('session:loadedContext');
  }

  applyClaims(claims) {
    this.roles = claims.roles ?? [];
    this.scopes = claims.scp ?? [];
    this.userId = claims.uid;
    this.personId = claims.sub;
    this.tenants = claims.tid ?? [];

    this.resolveSchoolId(claims);

    this.persistLoggingContext(claims);
  }

  resolveSchoolId(claims) {
    if (isArray(claims.tid) && !isEmpty(claims.tid)) {
      this.schoolId = claims.tid[0];
    } else if (!isEmpty(claims.tid)) {
      this.schoolId = claims.tid;
    }
  }

  persistLoggingContext(claims) {
    sessionStorage.setItem('logging:userId', claims.uid);
    sessionStorage.setItem('logging:scopes', JSON.stringify(claims.scp));
  }

  async loadContext() {
    // Load user/person and school context (school model + options) in parallel.
    // Errors are intentionally not caught here — a network failure loading context
    // does not mean the session is invalid. Let it propagate so callers can decide.
    await Promise.all([
      this.loadCoreModels(),
      this.loadSchoolContext(this.schoolId),
    ]);
  }

  async loadCoreModels() {
    const promises = [
      this.loadAndSet({ modelName: 'user', id: this.userId, prop: 'user' }),
      this.loadAndSet({
        modelName: 'person',
        id: this.personId,
        prop: 'person',
      }),
    ].filter(Boolean);

    await Promise.all(promises);
  }

  // loadSchoolContext loads the school model and its options. It is the single
  // place responsible for making school-level context ready, used by the
  // initial token flow (loadContext).
  async loadSchoolContext(schoolId) {
    if (isEmpty(schoolId)) return;
    await this.loadAndSet({
      modelName: 'school',
      id: schoolId,
      prop: 'school',
    });
    await this.options.load(schoolId);
  }

  async loadAndSet({ modelName, id, prop, onlyIfMissing = false }) {
    if (!id) return null;

    if (onlyIfMissing && this.get(prop)) {
      return null;
    }

    // Prefer local cache first, in theory findRecord should do this, but it's more
    // nuanced than that and I don't want to slam the network if ember decides
    // wants to send net requests.
    const existing = this.store.peekRecord(modelName, id);
    if (existing) {
      this.set(prop, existing);
      return Promise.resolve(existing);
    }

    // Fall back to network
    this.set(prop, await this.store.findRecord(modelName, id));
  }
}
